'use client';

import { Fragment, useEffect, useMemo, useState } from 'react';
import Link from 'next/link';
import { AnimatePresence, motion } from 'framer-motion';
import { stegaClean } from '@sanity/client/stega';
import Image from '@/helpers/Image';
import { cn } from '@/lib/utils';
import type { ITradingCompetitionPopup } from '@/sanity/lib/definitions';
import { DEFAULT_LANGUAGE, resolveI18nText } from '@/sanity/globals/I18n';
import { resolveLink } from '@/sanity/globals/CTA';
import { extractAffCode, withAffCode } from '@/lib/launchEvent';
import { sanitizeAffCode } from '@/lib/tradingCompetitionAffiliate';
import { useTradingCompetitionPopupLive } from '@/hooks/useTradingCompetitionPopupLive';
import {
  ArrowDots,
  EventCardBackground,
  pad,
  useTimeLeft,
  type TimeLeft,
} from '@/components/global/eventWidget/EventWidgetParts';
import trophy from '@/public/motions/trading-competition/hero-trophy.png';
import closeIcon from '@/public/trading-competition/popup/close.svg';

/**
 * Corner pop-up promoting the Trading Competition.
 *
 * Figma "Competition Pop-UP" (file RTqa1425vVOUAEVlKfKcnt), two layouts:
 * - Desktop (581x174, node 10446:3767, since deleted from the file): the
 *   LaunchEventWidget card (same gradient, noise, countdown and CTA — shared
 *   via EventWidgetParts) with a trophy column on the left and the close
 *   button in a right-hand column above the CTA.
 * - Mobile (358x108, nodes 11414:10052 EN / 11414:10142 DE): a relayout with
 *   no trophy — headline and countdown side by side over a full-width CTA, and
 *   the close button outside the card, above its top-right corner.
 *
 * Both are written at their Figma size (px / 8 → rem). `auto` renders both and
 * lets `lg` pick one; the previews force either.
 *
 * Scheduled by the Trading Competition Pop-up singleton
 * (sanity/globals/TradingCompetitionPopup.ts) and mounted on the homepage and
 * the partner pages (old-design affiliates, Fabio/WCE, Patrick): it shows
 * between Start and End Date while enabled, like the Launch Event widget. On a
 * partner page the CTA carries that page's `aff` code, which the competition
 * page then puts on its checkout links (lib/tradingCompetitionAffiliate.ts).
 *
 * Dismissal is per browser tab (sessionStorage): closing it should not sink
 * the campaign for good, but it should stay closed while the visitor browses.
 */

/**
 * Copy used when the document leaves a field (or its translation) empty. EN
 * matches the schema's initial values, DE the Figma DE frames; any other
 * language falls back to EN.
 */
const FALLBACK_COPY = {
  en: {
    eyebrow: 'IQ Capital',
    headline: 'Trading Competition',
    ctaLabel: 'JOIN NOW',
    daysLabel: 'DAYS',
    hoursLabel: 'HRS',
    minutesLabel: 'MIN',
    secondsLabel: 'SEC',
  },
  de: {
    eyebrow: 'IQ Capital',
    headline: 'Trading Competition',
    ctaLabel: 'JETZT TEILNEHMEN',
    daysLabel: 'TAGE',
    hoursLabel: 'STD',
    minutesLabel: 'MIN',
    secondsLabel: 'SEK',
  },
} as const;

type CopyKey = keyof typeof FALLBACK_COPY.en;

interface TradingCompetitionPopupProps {
  /** The "Trading Competition Pop-up" singleton (sanity/globals/TradingCompetitionPopup.ts). */
  data?: ITradingCompetitionPopup | null;
  language?: string;
  /**
   * Show it whatever the schedule says, and never remember a dismissal — for
   * /testing-pop-up, which must work before the document exists or while it
   * is disabled.
   */
  preview?: boolean;
  /** `auto` switches at `lg`; the others force one layout (previews). */
  variant?: 'auto' | 'desktop' | 'mobile';
  /**
   * `fixed` docks it bottom-left like the other corner widgets; `static`
   * renders it in place (previews).
   */
  position?: 'fixed' | 'static';
  /**
   * A partner page's checkout URL (`...?aff=CODE`). Its code goes onto the CTA
   * so the visitor reaches the competition page attributed to that partner,
   * as LaunchEventWidget does with the same prop.
   */
  affCtaUrl?: string;
  className?: string;
}

/** What both layouts render; resolved once so they always agree. */
interface CardContent {
  eyebrow: string;
  headline: string;
  ctaLabel: string;
  labels: [days: string, hours: string, minutes: string, seconds: string];
  closeLabel: string;
  timeLeft: TimeLeft | null;
  href: string;
  onDismiss: () => void;
  /** The DE desktop frame trades countdown width for its longer CTA. */
  german: boolean;
}

const DEFAULT_CTA_PATH = '/trading-competition';

export default function TradingCompetitionPopup({
  data,
  language,
  preview = false,
  variant = 'auto',
  position = 'fixed',
  affCtaUrl,
  className,
}: TradingCompetitionPopupProps) {
  const { config, live } = useTradingCompetitionPopupLive(data);
  const docked = position === 'fixed';
  const persist = docked && !preview;
  // The settings string carries stega characters in draft mode.
  const lang = stegaClean(language) || DEFAULT_LANGUAGE;
  const german = lang === 'de';

  // Undecided until the storage check has run, so a dismissed pop-up never
  // flashes on load. One key per campaign window, so the next campaign is not
  // hidden by a dismissal of the previous one.
  const [dismissed, setDismissed] = useState<boolean | null>(null);
  const storageKey = `tradingCompetitionPopupDismissed:${config?.startDate ?? config?.countdownTarget ?? 'default'}`;

  const ctaPath = config?.ctaPath || DEFAULT_CTA_PATH;
  const [href, setHref] = useState(ctaPath);

  const targetDate = useMemo(() => {
    const raw = config?.countdownTarget;
    if (!raw) return null;
    const date = new Date(raw);
    return Number.isFinite(date.getTime()) ? date : null;
  }, [config?.countdownTarget]);

  const timeLeft = useTimeLeft(targetDate);

  useEffect(() => {
    if (!persist) {
      setDismissed(false);
      return;
    }
    try {
      setDismissed(sessionStorage.getItem(storageKey) === '1');
    } catch {
      // Private mode / blocked storage: showing the pop-up is the safe default.
      setDismissed(false);
    }
  }, [persist, storageKey]);

  // resolveLink reads the visitor's URL and localStorage (UTM passthrough), so
  // it only runs in the browser — same pattern as LaunchEventWidget. The
  // partner page's own code then replaces any `aff` the visitor brought, the
  // same way that page's checkout buttons use it.
  const affCode = sanitizeAffCode(extractAffCode(stegaClean(affCtaUrl))) ?? undefined;
  useEffect(() => {
    setHref(withAffCode(resolveLink(ctaPath, lang), affCode));
  }, [ctaPath, lang, affCode]);

  const onDismiss = () => {
    setDismissed(true);
    if (!persist) return;
    try {
      sessionStorage.setItem(storageKey, '1');
    } catch {
      // Dismissal then lasts for this page view only — acceptable.
    }
  };

  // The document's text, then its translation; an empty DE translation falls
  // back to the DE copy above rather than to the English field.
  const copy = (key: CopyKey) => {
    const i18n = config?.[`${key}I18n` as const];
    return resolveI18nText(
      config?.[key] || FALLBACK_COPY.en[key],
      { ...i18n, de: i18n?.de || FALLBACK_COPY.de[key] },
      lang,
    );
  };

  const content: CardContent = {
    eyebrow: copy('eyebrow'),
    headline: copy('headline'),
    ctaLabel: copy('ctaLabel'),
    labels: [copy('daysLabel'), copy('hoursLabel'), copy('minutesLabel'), copy('secondsLabel')],
    closeLabel: german ? 'Schließen' : 'Close',
    timeLeft,
    href,
    onDismiss,
    german,
  };

  const shown = (preview || live) && dismissed === false;

  return (
    <AnimatePresence>
      {shown && (
        <motion.div
          role="complementary"
          aria-label={`${content.eyebrow} ${content.headline}`}
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          // No delay on the way out, or the X feels dead.
          exit={{ opacity: 0, transition: { duration: 0.2, ease: 'easeIn' } }}
          transition={{ type: 'spring', stiffness: 110, damping: 20, delay: 0.6 }}
          // z-[100]: above the homepage pricing section (z-[99]); the mobile
          // menu portal is also 100 but comes later in <body>, so it still
          // covers the pop-up when open.
          className={cn(
            docked
              ? 'fixed bottom-[2rem] left-[2rem] z-[100] lg:bottom-[3rem] lg:left-[3rem]'
              : 'relative w-fit',
            className,
          )}
        >
          {variant !== 'mobile' && (
            <DesktopCard content={content} className={variant === 'auto' ? 'hidden lg:block' : undefined} />
          )}
          {variant !== 'desktop' && (
            <MobileCard content={content} className={variant === 'auto' ? 'lg:hidden' : undefined} />
          )}
        </motion.div>
      )}
    </AnimatePresence>
  );
}

/**
 * Figma rounds every text box up to a whole pixel (24.84 → 25 for the
 * headline lines, 28.6 → 29 for the digits), so the boxes carry those heights.
 */
function DesktopCard({ content, className }: { content: CardContent; className?: string }) {
  const { german } = content;
  return (
    // Figma counts the 1px stroke towards the layout here, so 23px of inset
    // around a 537px row that runs 2px into the right padding — exactly as
    // the frame draws it.
    <div
      className={cn(
        'relative w-[72.625rem] overflow-hidden rounded-[0.5rem] p-[2.875rem] shadow-[0px_1.75rem_1.975rem_0px_rgba(0,0,0,0.08)]',
        className,
      )}
    >
      <EventCardBackground />
      {/* The stroke, painted over the gradient as Figma does. A CSS border
          would sit over the page instead (a white rim on light pages); an
          inset shadow does not. */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 rounded-[0.5rem] shadow-[inset_0_0_0_1px_rgba(255,255,255,0.14)]"
      />

      <div className="relative flex w-[67.125rem] items-end justify-between">
        {/* Trophy: a 101x128 window on the image, which runs 37.93% past the
            bottom (Figma crop, sy 0.725). */}
        <div className="pointer-events-none relative h-[16rem] w-[12.625rem] shrink-0 overflow-hidden" aria-hidden>
          <div className="absolute left-0 top-[0.04%] h-[137.93%] w-full">
            <Image
              src={trophy}
              alt=""
              fill
              quality={100}
              sizes="6vw"
              style={{ width: '100%', height: '100%' }}
            />
          </div>
        </div>

        {/* DE takes the DE frame's column (Figma 11414:9963: 231px against
            242) and CTA padding (16px against 20). That frame's 172px CTA
            column is narrower than its 184px CTA, which hangs out to the
            left; next to the trophy that would leave 5px before the
            countdown, so here the column hugs the CTA instead. */}
        <div
          className={cn('flex shrink-0 flex-col items-start gap-[2rem]', german ? 'w-[28.875rem]' : 'w-[30.25rem]')}
        >
          <div className="flex flex-col items-start gap-[0.375rem] whitespace-nowrap">
            <p className="mb-0 h-[3.125rem] font-blacker text-[3rem] font-normal italic leading-[1.035] tracking-[-0.08625rem] text-[#ffda74]">
              {content.eyebrow}
            </p>
            <p className="mb-0 h-[3.125rem] font-blacker text-[3rem] font-normal leading-[1.035] tracking-[-0.08625rem] text-white">
              {content.headline}
            </p>
          </div>

          <Countdown size="desktop" timeLeft={content.timeLeft} labels={content.labels} />
        </div>

        {/* Close on top, CTA at the bottom, across the row's height. */}
        <div
          className={cn(
            'flex shrink-0 flex-col items-end justify-between self-stretch',
            !german && 'w-[19.25rem]',
          )}
        >
          <button
            type="button"
            onClick={content.onDismiss}
            aria-label={content.closeLabel}
            className="flex h-[3.75rem] w-[3.75rem] shrink-0 items-center justify-center rounded-full bg-white/[0.06] shadow-[inset_0_0_0_1px_rgba(255,255,255,0.24)] backdrop-blur-[4px] transition-colors hover:bg-white/[0.14]"
          >
            <Image src={closeIcon} alt="" width={16} height={16} />
          </button>

          <Link
            href={content.href}
            className={cn(
              'flex shrink-0 items-center justify-center gap-[0.5rem] rounded-[0.375rem] bg-[#ffda74] py-[1.25rem] transition-colors hover:bg-[#ffe9a6]',
              german ? 'px-[2rem]' : 'px-[2.5rem]',
            )}
          >
            <span className="whitespace-nowrap font-geist text-[1.75rem] font-normal leading-[1] tracking-[-0.0175rem] text-black">
              {content.ctaLabel}
            </span>
            <ArrowDots />
          </Link>
        </div>
      </div>
    </div>
  );
}

/**
 * Figma frame 358.9x137.7: the card (358x108) with the close button above it,
 * 2.7px clear of its top edge and 0.9px past its right one. The card counts
 * its 0.9px stroke towards the inset (15.29 / 12.17px to the content) and has
 * a fixed height, so the content ends 10.37px above the bottom. Text boxes
 * are rounded up to whole pixels as Figma draws them (15.42 → 16, 19.93 → 20,
 * 12.59 → 13).
 */
function MobileCard({ content, className }: { content: CardContent; className?: string }) {
  return (
    <div className={cn('relative w-[44.8624rem] pt-[3.7104rem]', className)}>
      <button
        type="button"
        onClick={content.onDismiss}
        aria-label={content.closeLabel}
        className="absolute right-0 top-0 flex h-[3.3731rem] w-[3.3731rem] items-center justify-center rounded-full bg-[#11332a] shadow-[inset_0_0_0_0.1124rem_rgba(255,255,255,0.24)] backdrop-blur-[3.598px] transition-colors hover:bg-[#1a463b]"
      >
        <Image
          src={closeIcon}
          alt=""
          width={16}
          height={16}
          style={{ width: '1.799rem', height: '1.799rem' }}
        />
      </button>

      <div className="relative h-[13.5rem] w-[44.75rem] overflow-hidden rounded-[0.4497rem] px-[1.9114rem] pt-[1.5217rem] shadow-[0px_1.5741rem_1.7765rem_0px_rgba(0,0,0,0.08)]">
        <EventCardBackground />
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0 rounded-[0.4497rem] shadow-[inset_0_0_0_0.1124rem_rgba(255,255,255,0.14)]"
        />

        <div className="relative flex flex-col gap-[1.4616rem]">
          <div className="flex items-center justify-between">
            <div className="flex min-w-0 flex-1 flex-col items-start gap-[0.787rem] whitespace-nowrap">
              <p className="mb-0 h-[2rem] font-blacker text-[2.0869rem] font-normal italic leading-[1.9274rem] tracking-[-0.0535rem] text-[#ffda74]">
                {content.eyebrow}
              </p>
              <p className="mb-0 h-[2rem] font-blacker text-[2.0869rem] font-normal leading-[1.9274rem] tracking-[-0.0535rem] text-white">
                {content.headline}
              </p>
            </div>

            <Countdown size="mobile" timeLeft={content.timeLeft} labels={content.labels} />
          </div>

          <Link
            href={content.href}
            className="flex w-full items-center justify-center gap-[0.4497rem] rounded-[0.3372rem] bg-[#ffda74] px-[2.2488rem] py-[0.8995rem] transition-colors hover:bg-[#ffe9a6]"
          >
            <span className="h-[1.625rem] whitespace-nowrap font-geist text-[1.3493rem] font-normal leading-[1.5741rem] tracking-[-0.0135rem] text-black">
              {content.ctaLabel}
            </span>
            {/* The desktop glyph at the frame's 0.8995 scale (1.384px dots on
                a 2.77px step). */}
            <span className="block shrink-0 [zoom:0.8995]">
              <ArrowDots />
            </span>
          </Link>
        </div>
      </div>
    </div>
  );
}

/**
 * The bar has no start date to measure against, so it keeps each frame's
 * sliver (94/242 desktop, 65.5/139.4 mobile). Figma gives every unit its own
 * fixed width.
 */
const COUNTDOWN = {
  desktop: {
    block: 'h-[7.375rem] w-full',
    bar: 'h-[0.25rem] rounded-[0.25rem]',
    fill: 'w-[38.84%]',
    units: ['w-[4.5rem]', 'w-[4.375rem]', 'w-[3.875rem]', 'w-[3.875rem]'],
    unit: 'h-[6rem] gap-[0.375rem]',
    value: 'h-[3.625rem] text-[3.25rem] leading-[1.1] tracking-[-0.0325rem]',
    label: 'h-[1.75rem] text-[1.5rem] leading-[1.1667] tracking-[0.035rem]',
    colon: 'text-[2rem] leading-[1.7]',
  },
  mobile: {
    block: 'h-[5.1721rem] w-[17.4278rem] shrink-0',
    bar: 'h-[0.1742rem] rounded-[0.1742rem]',
    fill: 'w-[46.98%]',
    units: ['w-[3.1359rem]', 'w-[3.0488rem]', 'w-[2.7004rem]', 'w-[2.7004rem]'],
    unit: 'h-[4.1811rem] gap-[0.2613rem]',
    value: 'h-[2.5rem] text-[2.2488rem] leading-[2.4913rem] tracking-[-0.0227rem]',
    label: 'h-[1.2195rem] text-[1.0453rem] leading-[1.2195rem] tracking-[0.0244rem]',
    colon: 'text-[1.3938rem] leading-[2.3694rem]',
  },
} as const;

function Countdown({
  size,
  timeLeft,
  labels,
}: {
  size: keyof typeof COUNTDOWN;
  timeLeft: TimeLeft | null;
  labels: CardContent['labels'];
}) {
  const s = COUNTDOWN[size];
  const values = timeLeft ? [timeLeft.days, timeLeft.hours, timeLeft.minutes, timeLeft.seconds] : null;
  return (
    <div className={cn('flex flex-col justify-between', s.block)}>
      <div className={cn('w-full overflow-hidden bg-[rgba(152,152,152,0.4)]', s.bar)}>
        <div className={cn('h-full bg-[#5ae4bf]', s.fill)} />
      </div>

      {values && (
        <div className="flex w-full items-start justify-between">
          {values.map((value, i) => (
            <Fragment key={i}>
              {i > 0 && <p className={cn('mb-0 shrink-0 font-geist text-white/70', s.colon)}>:</p>}
              <div className={cn('flex shrink-0 flex-col items-center', s.unit, s.units[i])}>
                <p className={cn('mb-0 whitespace-nowrap font-geist font-normal text-white', s.value)}>{pad(value)}</p>
                <p className={cn('mb-0 whitespace-nowrap font-geist font-normal uppercase text-white/70', s.label)}>
                  {labels[i]}
                </p>
              </div>
            </Fragment>
          ))}
        </div>
      )}
    </div>
  );
}
